'use client';

import { usePathname } from 'next/navigation';
import { useState } from 'react';
import Slider from 'react-slick';

import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import useLocalStorage from '@/hooks/useLocalStorage';
import { cn } from '@/lib/utils';
import './styles.css';

import { Village } from '@/services/types';
import { VillageCardDictonary } from '../VillageCard/Root';
import { WishList } from '../VillageCard/Options';
import VillageCard from '../VillageCard';
import { VILLA_CARD_IMAGE_SIZES } from '../VillageCard/image-sizes';

import VillageGallery from '../VillageGallery';

type Props = {
  featuredVillages: Village[];
  dicts: VillageCardDictonary;
  lang: string;
};

export default function FeaturedVillagesCarousel({
  featuredVillages,
  dicts,
  lang,
}: Props) {
  const [sliderRef, setSliderRef] = useState<Slider | null>(null);
  const [carouselLastIndex, setCarouselLastIndex] = useState(
    featuredVillages.length - 1,
  );
  const [wishList, setWishList] = useLocalStorage('villas', [] as WishList[]);
  const [villageInfo, setVillageInfo] = useState<Village>();
  const [slickIndex, setSlickIndex] = useState<number>(0);
  const pathname = usePathname();

  const settings = {
    dots: false,
    infinite: false,
    speed: 1500,
    slidesToShow: 3,
    slidesToScroll: 3,
    arrows: false,
    beforeChange: (prev: number, next: number) => {
      setCarouselLastIndex(featuredVillages.length - 3);
      setSlickIndex(next);
    },
    responsive: [
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          beforeChange: (prev: number, next: number) => {
            setCarouselLastIndex(featuredVillages.length - 1);
            setSlickIndex(next);
          },
        },
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          beforeChange: (prev: number, next: number) => {
            setCarouselLastIndex(featuredVillages.length - 2);
            setSlickIndex(next);
          },
        },
      },
    ],
  };

  return (
    <div className="w-full h-full relative">
      {villageInfo && (
        <VillageGallery
          village={villageInfo}
          handleClose={() => setVillageInfo(undefined)}
          dicts={dicts}
        />
      )}
      <button
        id="fv-carousel-previous-button"
        aria-label="Carousel previous button"
        className={cn(
          'rounded-full z-10 text-zinc-400 flex justify-center group items-center bg-white shadow-lg overflow-hidden absolute top-[300px] desktop:top-[320px] left-[4px] desktop:left-[-45px] translate-y-[-50%] desktop:translate-y-none desktop:translate-x-[50%] w-11 h-[44px] transition-opacity duration-300 ease-in-out opacity-1 scale-100',
          {
            'opacity-0 scale-0': slickIndex === 0,
          },
        )}
        type="button"
        onClick={sliderRef?.slickPrev}>
        <FiChevronLeft
          aria-label="Chevron Left Icon"
          className="w-6 h-6 desktop:w-8 desktop:h-8 stroke-slate-600 opacity-30 group-hover:opacity-20 transition"
        />
      </button>

      <button
        id="fv-carousel-next-button"
        aria-label="Carousel next button"
        className={cn(
          'rounded-full z-10 text-zinc-400 flex justify-center group items-center bg-white shadow-lg overflow-hidden absolute top-[300px] desktop:top-[320px] right-[4px] translate-y-[-50%] desktop:translate-y-none desktop:translate-x-[50%] w-11 h-[44px] transition-opacity duration-300 ease-in-out opacity-1 scale-100',
          {
            'opacity-0 scale-0': slickIndex === carouselLastIndex,
          },
        )}
        type="button"
        onClick={sliderRef?.slickNext}>
        <FiChevronRight
          aria-label="Chevron Right Icon"
          className="w-6 h-6 desktop:w-8 desktop:h-8 stroke-slate-600 opacity-30 group-hover:opacity-20 transition"
        />
      </button>

      <Slider
        ref={setSliderRef}
        className={cn('featured-villages-carousel text-center')}
        {...settings}>
        {featuredVillages.map((village, index) => (
          <VillageCard.Root
            lang={lang}
            dicts={dicts}
            key={index}
            infos={village}
            imageSizes={VILLA_CARD_IMAGE_SIZES.featured}
            atFeaturedVillages>
            <VillageCard.Options
              wishList={wishList}
              setWishList={setWishList}
              onGalleryClick={setVillageInfo}
              village={village}
            />
          </VillageCard.Root>
        ))}
      </Slider>
    </div>
  );
}
